import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ManagingPartnerSection } from '../components/about/ManagingPartnerSection';
import { blogPosts } from '../data/mockData';
import { ArrowRight, Download, BookOpen, Award, Users, Globe } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl" />
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h4 className="text-emerald-400 font-medium mb-4">The Pioneers in Organizational Performance Intelligence</h4>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                SAMANVAI = 
                <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Strategic AI-Managed Analysis & Navigation for Value-Added Insights
                </span>
              </h1>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Request a Free Demo
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  At SAMANVAI, we empower CXOs and executive teams to lead with insight, understand the performance landscape of their organizations, and connect strategies to what stakeholders care about most. We're more than a performance intelligence company—we're your partner in transforming how organizational value is defined, measured, and delivered.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Our name—reflecting Strategic AI-Managed Analysis & Navigation—embodies our roots: a deep expertise in organizational performance, financial storytelling, and AI-powered strategy. For over two decades, we've equipped executives with the tools and thinking to go beyond metrics—to craft strategies that drive outcomes.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Today, that mission lives on through our AI-powered platform featuring <strong>Performance Insights</strong>, our advanced benchmarking through <strong>Benchmark Intelligence</strong>, and our impact analysis through <strong>Impact Intelligence</strong>. Every solution is designed to help you gain stakeholder trust, accelerate improvements, and drive outcomes across the entire organizational lifecycle.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=600"
                  alt="Team collaboration"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">128+</div>
                      <div className="text-sm text-gray-600">Countries Served</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section data-testid="founder-section" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Photo Column */}
              <div className="order-2 lg:order-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_b22f4484-74c2-46b7-9774-f3a168856937/artifacts/263kg6ly_VK_2.jpg"
                  alt="Dr. Vijay Karna"
                  className="rounded-2xl shadow-xl w-full max-w-md mx-auto"
                />
                <div className="mt-6 text-center">
                  <h3 className="text-xl font-bold text-gray-900">Dr. Vijay Karna</h3>
                  <p className="text-emerald-600 font-medium">Founder & President</p>
                </div>
              </div>
              
              {/* Bio Column */}
              <div className="order-1 lg:order-2">
                <h4 className="text-emerald-600 font-medium mb-2">Our Visionary Leader</h4>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                  Dr. Vijay Karna
                </h2>
                
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  <strong>Dr. Vijay Karna</strong> is the Founder and President of <strong>SAMANVAI</strong>, an Agentic AI–powered executive intelligence platform designed to align insight, strategy, and enterprise value. A senior management consultant and <strong>Global Capability Center (GCC) Delivery & Transformation Leader</strong>, he brings <strong>over 30 years of experience</strong> advising Fortune 500 and mid-size enterprises across energy, utilities, banking, and industrial sectors.
                </p>
                
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Dr. Karna has built and governed large multi-service GCCs, scaling operations to <strong>5,000+ professionals</strong>, and has led complex digital transformation programs linking technology change directly to <strong>measurable business outcomes, risk mitigation, and operational excellence</strong>. His expertise spans digital readiness assessments, operating-model design, large-deal solution architecture, and organization change management.
                </p>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  Recognizing the gap between data availability and executive decision quality, he founded <strong>SAMANVAI</strong> to move leaders beyond dashboards toward <strong>synthesized, executive-grade intelligence and credible value articulation</strong>.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ManagingPartnerSection />

        {/* Thought Leadership Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Thought Leadership That Fuels Enterprise Growth
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                At SAMANVAI, we don't just follow industry trends, we help define them.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Book 1 */}
              <div data-testid="thought-leadership-book-first" className="bg-gray-50 rounded-2xl p-8 flex flex-col lg:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <div className="w-40 h-56 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg shadow-xl flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Performance-Led Leadership</h3>
                  <p className="text-gray-600 mb-4">
                    Our comprehensive guide is a trusted resource for CXOs who want to elevate their organizations, align with industry best practices, and lead with confidence.
                  </p>
                  <button className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700">
                    <Download className="w-4 h-4" />
                    Download Free Copy
                  </button>
                </div>
              </div>

              {/* Book 2 */}
              <div data-testid="thought-leadership-book-second" className="bg-gray-50 rounded-2xl p-8 flex flex-col lg:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <div className="w-40 h-56 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-lg shadow-xl flex items-center justify-center">
                    <Award className="w-16 h-16 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">7 Steps to Building a Top-Performing Organization</h3>
                  <p className="text-gray-600 mb-4">
                    Differentiating from competition requires dramatic action. Our eBook provides CXOs with a guide to transforming your organization into a performance powerhouse.
                  </p>
                  <button className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700">
                    <Download className="w-4 h-4" />
                    Download Free eBook
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Leading the Conversation on
                  <span className="block text-emerald-600">Performance-Driven Leadership</span>
                </h2>
              </div>
              <Link to="/resources" className="hidden md:inline-flex items-center text-emerald-600 font-medium hover:text-emerald-700">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <article key={post.id} className="group">
                  <div className="h-48 rounded-xl overflow-hidden mb-4">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                    <span>{post.date}</span>
                    <span className="text-emerald-600">{post.category}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">
                    {post.title}
                  </h3>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
